import { useContext, useMemo, useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { AutoAwesome as WizardIcon, TableRows as StandardIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import AuthContext from '../../context/authContext';
import EditableList from '../EditableList/EditableList';
import PriceOverrideTable from '../EditableList/EditableDisplayElements/PriceOverrideTable';
import ProductCreationWizard from '../Products/ProductCreationWizard';
import useHttp from '../../hooks/useHttp';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function ProductManagement() {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showStandardDialog, setShowStandardDialog] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Standard dialog form state
  const [standardFormData, setStandardFormData] = useState({
    internalName: '',
    name: '',
    description: '',
    validFrom: dayjs().format('YYYY-MM-DD'),
    validTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
  });

  // Memoized handlers to prevent unnecessary re-renders
  const handleCreateClick = useCallback(() => {
    setEditProduct(null);
    setShowChoiceModal(true);
  }, []);

  const handleChooseWizard = useCallback(() => {
    setShowChoiceModal(false);
    setShowWizard(true);
  }, []);

  const handleChooseStandard = useCallback(() => {
    setShowChoiceModal(false);
    setStandardFormData({
      internalName: '',
      name: '',
      description: '',
      validFrom: dayjs().format('YYYY-MM-DD'),
      validTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    });
    setShowStandardDialog(true);
  }, []);

  const handleWizardClose = useCallback(() => {
    setShowWizard(false);
    setEditProduct(null);
  }, []);

  const handleWizardSuccess = useCallback(() => {
    setShowWizard(false);
    setEditProduct(null);
  }, []);

  const handleEditWithWizard = useCallback(product => {
    setEditProduct(product);
    setShowWizard(true);
  }, []);

  const handleStandardSave = useCallback(async () => {
    if (!standardFormData.internalName.trim() || !standardFormData.name.trim()) {
      toast.error('Interner Name und Name sind erforderlich');
      return;
    }

    setIsSaving(true);
    try {
      const response = await sendRequest({
        url: '/api/v1/product',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {
          internalName: standardFormData.internalName,
          name: standardFormData.name,
          description: standardFormData.description,
          validFrom: standardFormData.validFrom,
          validTo: standardFormData.validTo,
        },
      });

      if (response && response._id) {
        toast.success('Produkt erstellt');
        setShowStandardDialog(false);
        window.dispatchEvent(
          new CustomEvent('editableListRefresh', { detail: { resource: 'product' } })
        );
      }
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Fehler beim Erstellen des Produkts');
    } finally {
      setIsSaving(false);
    }
  }, [standardFormData, sendRequest, authCtx]);

  const handleCloseChoiceModal = useCallback(() => setShowChoiceModal(false), []);
  const handleCloseStandardDialog = useCallback(() => setShowStandardDialog(false), []);

  // Memoize the parentIsEditable to prevent unnecessary re-renders
  const parentIsEditable = useMemo(() => authCtx.checkRight('product:update'), [authCtx]);

  if (!authCtx.checkRight('product:read')) {
    return <Navigate to='/welcome' replace />;
  }

  // Helper: Create transformValue for packages/upsells that includes priceOverrides
  const createTransformValue = useCallback(overrideField => {
    return (selectedValues, options, item) => {
      const array = Array.isArray(selectedValues)
        ? selectedValues
        : [selectedValues].filter(Boolean);
      const priceOverrides = item?.[overrideField] || {};
      return array.map(val => {
        const found = (options || []).find(opt => opt.value === val);
        const upgradeId = found ? found.value : val;
        const result = { upgradeId, name: found ? found.label : '' };
        if (priceOverrides[upgradeId] !== undefined && priceOverrides[upgradeId] !== null) {
          result.priceOverride = priceOverrides[upgradeId];
        }
        return result;
      });
    };
  }, []);

  // Helper: Create getSourceOptions for priceOverride fields
  const createGetSourceOptions = useCallback((sourceField, apiFieldName) => {
    return (item, apiOptions) => {
      const items = item?.[sourceField] || [];
      return items.map(itemEntry => {
        const upgradeId = itemEntry.upgradeId || itemEntry._id || itemEntry.value;
        const upgradeOption = (apiOptions?.[apiFieldName] || []).find(
          opt => opt.value === upgradeId
        );
        return {
          upgradeId,
          name: itemEntry.name || upgradeOption?.label || '',
          price: upgradeOption?.price || itemEntry.price || itemEntry.upgrade?.price || 0,
          upgrade: upgradeOption || itemEntry.upgrade || itemEntry,
        };
      });
    };
  }, []);

  // Helper: Create transformValue for priceOverride fields
  const createPriceOverrideTransformValue = useCallback(sourceField => {
    return (value, options, item) => {
      if (!value || typeof value !== 'object') return {};
      const items = item?.[sourceField] || [];
      const result = {};
      items.forEach(itemEntry => {
        const upgradeId = itemEntry.upgradeId || itemEntry._id || itemEntry.value;
        if (value[upgradeId] !== undefined && value[upgradeId] !== null) {
          result[upgradeId] = parseFloat(value[upgradeId]) || 0;
        }
      });
      return result;
    };
  }, []);

  // Helper: Create listDisplayValue for priceOverride fields
  const priceOverrideListDisplayValue = value => {
    if (!value || typeof value !== 'object') return '—';
    const count = Object.keys(value).length;
    return count > 0 ? `${count} Override${count !== 1 ? 's' : ''}` : '—';
  };

  // Helper: Create listDisplayValue for multi-select fields
  const multiSelectListDisplayValue = values => {
    if (!Array.isArray(values) || values.length === 0) return '—';
    return values
      .map(v => v?.name || '')
      .filter(Boolean)
      .join(', ');
  };

  // Memoized helper functions for schema
  const arrayDisplayValue = useCallback(
    value => (Array.isArray(value) ? value.join(', ') : '—'),
    []
  );
  const arrayTransformValue = useCallback(
    value =>
      value
        .toString()
        .split(',')
        .map(v => v.trim())
        .filter(Boolean),
    []
  );
  const resourceListDisplayValue = useCallback(values => {
    if (!Array.isArray(values) || values.length === 0) return '—';
    return values
      .map(v => v?.name || v?.label || '')
      .filter(Boolean)
      .join(', ');
  }, []);
  const singleOfferTransformValue = useCallback((selectedValue, options) => {
    if (!selectedValue) return null;
    const found = (options || []).find(opt => opt.value === selectedValue);
    return {
      upgradeId: found ? found.value : selectedValue,
      name: found ? found.label : '',
    };
  }, []);
  const singleOfferListDisplayValue = useCallback(value => value?.name || '—', []);

  const productSchema = useMemo(
    () => ({
      internalName: {
        label: 'Interner Name',
        type: 'text',
        required: true,
        minWidth: '200px',
      },
      name: {
        label: 'Name',
        type: 'text',
        required: true,
        minWidth: '220px',
      },
      sortPriority: {
        label: 'Sortierung',
        type: 'number',
        required: false,
        minWidth: '120px',
        placeholder: 'z.B. 10',
      },
      selfServiceMoveCancelDisabled: {
        label: 'Verschieben & Stornieren im Selfservice deaktivieren',
        type: 'boolean',
        required: false,
        minWidth: '260px',
      },
      alwaysRequestBooking: {
        label: 'Immer Buchungsanfrage',
        api: { itemField: 'alwaysRequestBooking' },
        type: 'boolean',
        required: false,
        minWidth: '170px',
      },
      ignoreEntertainmentOfflinePaymentFee: {
        label: 'Offline-Zahlungsgebühr vom Entertainment ignorieren',
        api: { itemField: 'ignoreEntertainmentOfflinePaymentFee' },
        type: 'boolean',
        required: false,
        minWidth: '320px',
      },
      description: {
        label: 'Beschreibung',
        type: 'text',
        required: false,
        minWidth: '360px',
        previewInList: true,
        previewMaxLines: 3,
        useHyphens: true,
      },
      tags: {
        label: 'Tags (kommagetrennt)',
        type: 'array',
        placeholder: 'z.B. karaoke, ...',
        displayValue: arrayDisplayValue,
        transformValue: arrayTransformValue,
      },
      minParticipants: {
        label: 'Min. Teilnehmer',
        type: 'number',
        required: false,
        minWidth: '140px',
      },
      maxParticipants: {
        label: 'Max. Teilnehmer',
        type: 'number',
        required: false,
        minWidth: '140px',
      },
      ageSplitMapping: {
        label: 'Altersgruppen-Zuordnung',
        type: 'object',
        schema: {
          kids: {
            label: 'Kinder-Kategorien',
            type: 'array',
            placeholder: 'z.B. U10, U14',
            displayValue: arrayDisplayValue,
            transformValue: arrayTransformValue,
          },
          adults: {
            label: 'Erwachsenen-Kategorien',
            type: 'array',
            placeholder: 'z.B. Ü14, Erwachsene',
            displayValue: arrayDisplayValue,
            transformValue: arrayTransformValue,
          },
        },
      },
      validFrom: {
        label: 'Gültig ab',
        type: 'date',
        required: true,
        minWidth: '160px',
      },
      validTo: {
        label: 'Gültig bis',
        type: 'date',
        required: true,
        minWidth: '160px',
      },
      pricing: {
        label: 'Preislisten',
        type: 'nested',
        addButtonText: 'Neue Preisliste hinzufügen',
        excludeFromAdd: true,
        api: {
          itemField: 'pricing',
          nestedEndpoint: 'pricing',
        },
        schema: {
          list: {
            label: 'Listenname',
            type: 'text',
            required: true,
          },
          title: {
            label: 'Titel (Booking)',
            type: 'text',
            required: false,
            placeholder: 'z. B. LaserNight',
            minWidth: '220px',
          },
          description: {
            label: 'Beschreibung (Booking)',
            type: 'text',
            required: false,
            minWidth: '360px',
            previewInList: true,
            previewMaxLines: 3,
            useHyphens: true,
          },
          fsk: {
            label: 'FSK',
            type: 'text',
            required: false,
            placeholder: 'z. B. FSK 16',
            minWidth: '140px',
          },
          sortKey: {
            label: 'Sortierung',
            type: 'number',
            required: false,
            placeholder: 'z. B. 10',
            minWidth: '140px',
          },
          groupType: {
            label: 'Gruppentyp',
            type: 'select',
            required: false,
            minWidth: '160px',
            options: [
              { value: '', label: 'Keiner' },
              { value: 'kids', label: 'Kinder' },
              { value: 'adults', label: 'Erwachsene' },
            ],
            transformValue: value => value || null,
          },
          excludedAgeGroups: {
            label: 'Ausgeschl. Altersgruppen',
            type: 'array',
            placeholder: 'kids, adults, ...',
            displayValue: arrayDisplayValue,
            transformValue: arrayTransformValue,
          },
          tags: {
            label: 'Tags (kommagetrennt)',
            type: 'array',
            placeholder: 'bestseller, sommer, ...',
            displayValue: arrayDisplayValue,
            transformValue: arrayTransformValue,
          },
          categories: {
            label: 'Kategorien',
            type: 'nested',
            addButtonText: 'Kategorie hinzufügen',
            excludeFromAdd: true,
            api: {
              itemField: 'categories',
              nestedEndpoint: 'category',
            },
            schema: {
              name: { label: 'Name', type: 'text', required: true },
              shortName: { label: 'Kurzname', type: 'text', required: true },
              description: { label: 'Beschreibung', type: 'text', required: true },
              hint: {
                label: 'Hinweis (unter Kategoriename im Buchungswidget)',
                type: 'text',
                required: false,
              },
              sortKey: {
                label: 'Sortierung',
                type: 'number',
                required: false,
              },
            },
          },
          durations: {
            label: 'Dauern',
            type: 'nested',
            addButtonText: 'Dauer hinzufügen',
            excludeFromAdd: true,
            api: {
              itemField: 'durations',
              nestedEndpoint: 'duration',
            },
            schema: {
              durationMS: { label: 'Dauer (Min)', type: 'milliseconds', required: true },
              recommendedDurationUnit: {
                label: 'Empf. Einheit',
                type: 'text',
                required: true,
              },
              durationDescription: { label: 'Beschreibung', type: 'text', required: false },
              customerDuration: {
                label: 'Anzeige für Kunden (Min)',
                type: 'milliseconds',
                required: false,
                placeholder: 'z. B. 90',
              },
              isBestseller: { label: 'Bestseller', type: 'boolean', required: false },
              isUserBookingRestricted: {
                label: 'Customer-Buchung beschränken',
                type: 'boolean',
                required: false,
              },
            },
          },
          priceMatrix: {
            label: 'Preismatrix',
            type: 'nested',
            addButtonText: 'Matrix-Eintrag hinzufügen',
            excludeFromAdd: true,
            api: {
              itemField: 'priceMatrix',
              nestedEndpoint: 'priceMatrix',
            },
            schema: {
              category: { label: 'Kategorie', type: 'text', required: true },
              durationMS: { label: 'Dauer (Min)', type: 'milliseconds', required: true },
              prices: {
                label: 'Staffelpreise',
                type: 'nested',
                addButtonText: 'Preis hinzufügen',
                excludeFromAdd: true,
                api: {
                  itemField: 'prices',
                  nestedEndpoint: 'price',
                },
                schema: {
                  minPersons: { label: 'Min Personen', type: 'number', required: true },
                  price: { label: 'Preis', type: 'price', required: true, minWidth: '140px' },
                },
              },
              minPrice: { label: 'Min Preis', type: 'price', readOnly: true, excludeFromAdd: true },
            },
          },
          paymentOptions: {
            label: 'Zahlungsarten (kommagetrennt)',
            type: 'array',
            placeholder: 'online, offline',
            displayValue: arrayDisplayValue,
            transformValue: arrayTransformValue,
          },
          onlinePaymentOptions: {
            label: 'Online Zahlungsarten (kommagetrennt)',
            type: 'array',
            placeholder: 'card, paypal, ApplePay',
            displayValue: arrayDisplayValue,
            transformValue: arrayTransformValue,
          },
          offlinePaymentFee: {
            label: 'Offline-Zahlungsgebühr',
            type: 'price',
            required: false,
          },
        },
      },
      resourceIds: {
        label: 'Ressourcen',
        type: 'multi-select',
        excludeFromAdd: true,
        api: {
          resource: 'resource',
          labelField: 'name',
          valueField: '_id',
          skipSpecial: true,
        },
        placeholder: 'Ressourcen auswählen',
        required: false,
        listDisplayValue: resourceListDisplayValue,
      },
      orderedResourceIds: {
        label: 'Geordnete Ressourcen',
        type: 'multi-select',
        excludeFromAdd: true,
        api: {
          resource: 'resource',
          labelField: 'name',
          valueField: '_id',
          skipSpecial: true,
        },
        placeholder: 'Geordnete Ressourcen auswählen',
        required: false,
        listDisplayValue: resourceListDisplayValue,
      },
      maxBookingGroupsOverride: {
        label: 'Max. parallel nutzbare Ressourcen (Override)',
        placeholder: 'Leer = Wert vom Entertainment',
        type: 'number',
        required: false,
        excludeFromAdd: true,
        minWidth: '280px',
      },
      singleOffer: {
        label: 'Einzelangebot',
        type: 'select',
        excludeFromAdd: true,
        api: {
          resource: 'upgrade',
          idName: 'upgradeId',
          labelField: 'name',
          valueField: '_id',
          skipSpecial: true,
        },
        placeholder: 'Upgrade auswählen',
        required: false,
        transformValue: singleOfferTransformValue,
        listDisplayValue: singleOfferListDisplayValue,
      },
      ticketInsuranceId: {
        label: 'Ticketversicherung',
        type: 'select',
        excludeFromAdd: true,
        excludeFromList: true,
        api: {
          resource: 'upgrade',
          labelField: 'name',
          valueField: '_id',
          skipSpecial: true,
        },
        placeholder: 'Ticketversicherung-Upgrade auswählen',
        required: false,
      },
      packages: {
        label: 'Pakete',
        type: 'multi-select',
        api: {
          fieldName: 'upgrade',
          itemField: 'packages',
          idName: 'upgradeId',
          resource: 'upgrade',
          labelField: 'name',
          valueField: '_id',
          skipSpecial: true,
          objectify: { idProp: 'upgradeId', labelProp: 'name' },
        },
        placeholder: 'Pakete auswählen',
        required: false,
        transformValue: createTransformValue('packagePriceOverrides'),
        listDisplayValue: multiSelectListDisplayValue,
      },
      packagePriceOverrides: {
        label: 'Paket Preisüberschreibungen',
        type: 'price-override-table',
        sourceField: 'packages',
        required: false,
        excludeFromAdd: true,
        excludeFromList: true,
        listDisplayValue: priceOverrideListDisplayValue,
        getSourceOptions: createGetSourceOptions('packages', 'packages'),
        transformValue: createPriceOverrideTransformValue('packages'),
      },
      upsells: {
        label: 'Upsells',
        type: 'multi-select',
        api: {
          fieldName: 'upgrade',
          itemField: 'upsells',
          idName: 'upgradeId',
          resource: 'upgrade',
          labelField: 'name',
          valueField: '_id',
          skipSpecial: true,
          objectify: { idProp: 'upgradeId', labelProp: 'name' },
        },
        placeholder: 'Upsells auswählen',
        required: false,
        transformValue: createTransformValue('upsellPriceOverrides'),
        listDisplayValue: multiSelectListDisplayValue,
      },
      upsellPriceOverrides: {
        label: 'Upsell Preisüberschreibungen',
        type: 'price-override-table',
        sourceField: 'upsells',
        required: false,
        excludeFromAdd: true,
        excludeFromList: true,
        listDisplayValue: priceOverrideListDisplayValue,
        getSourceOptions: createGetSourceOptions('upsells', 'upsells'),
        transformValue: createPriceOverrideTransformValue('upsells'),
      },
    packageOverrides: {
      label: 'Paket-Overrides (pro Entertainment)',
      type: 'nested',
      addButtonText: 'Override hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'packageOverrides',
        nestedEndpoint: 'packageOverride',
      },
      schema: {
        entertainmentId: {
          label: 'Entertainment',
          type: 'select',
          required: true,
          api: {
            resource: 'entertainment',
            labelField: 'longName',
            valueField: '_id',
            skipSpecial: true,
          },
          placeholder: 'Entertainment auswählen',
        },
        upgradeId: {
          label: 'Paket',
          type: 'select',
          required: true,
          // Only offer packages that are assigned to the product itself
          filterOptionsByParentField: 'packages',
          api: {
            resource: 'upgrade',
            labelField: 'name',
            valueField: '_id',
            skipSpecial: true,
          },
          placeholder: 'Paket auswählen',
        },
        priceOverride: {
          label: 'Preis (Override)',
          type: 'price',
          required: false,
        },
        descriptionShort: {
          label: 'Kurzbeschreibung',
          type: 'text',
          required: false,
        },
        description: {
          label: 'Beschreibung',
          type: 'text',
          required: false,
        },
      },
    },
    upsellOverrides: {
      label: 'Upsell-Overrides (pro Entertainment)',
      type: 'nested',
      addButtonText: 'Override hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'upsellOverrides',
        nestedEndpoint: 'upsellOverride',
      },
      schema: {
        entertainmentId: {
          label: 'Entertainment',
          type: 'select',
          required: true,
          api: {
            resource: 'entertainment',
            labelField: 'longName',
            valueField: '_id',
            skipSpecial: true,
          },
          placeholder: 'Entertainment auswählen',
        },
        upgradeId: {
          label: 'Upsell',
          type: 'select',
          required: true,
          // Only offer upsells that are assigned to the product itself
          filterOptionsByParentField: 'upsells',
          api: {
            resource: 'upgrade',
            labelField: 'name',
            valueField: '_id',
            skipSpecial: true,
          },
          placeholder: 'Upsell auswählen',
        },
        priceOverride: {
          label: 'Preis (Override)',
          type: 'price',
          required: false,
        },
        descriptionShort: {
          label: 'Kurzbeschreibung',
          type: 'text',
          required: false,
        },
        description: {
          label: 'Beschreibung',
          type: 'text',
          required: false,
        },
      },
    },
    }),
    [
      arrayDisplayValue,
      arrayTransformValue,
      resourceListDisplayValue,
      singleOfferTransformValue,
      singleOfferListDisplayValue,
      createTransformValue,
      createGetSourceOptions,
      createPriceOverrideTransformValue,
      multiSelectListDisplayValue,
      priceOverrideListDisplayValue,
    ]
  );

  // Extract priceOverrides from packages/upsells arrays into separate fields
  const transformItems = useCallback(item => {
    const processed = { ...item };

    // Helper to extract priceOverrides from an array
    const extractOverrides = (arr, targetField) => {
      if (!arr || !Array.isArray(arr)) return;
      const overrides = {};
      arr.forEach(entry => {
        const upgradeId = entry.upgradeId || entry._id;
        if (entry.priceOverride !== undefined && entry.priceOverride !== null) {
          overrides[upgradeId] = entry.priceOverride;
        }
      });
      if (Object.keys(overrides).length > 0) {
        processed[targetField] = overrides;
      }
    };

    extractOverrides(item.packages, 'packagePriceOverrides');
    extractOverrides(item.upsells, 'upsellPriceOverrides');

    return processed;
  }, []);

  // Apply priceOverrides to packages/upsells when they are updated
  const transformFieldUpdate = useCallback((key, newValue, item) => {
    const overrideMap = {
      packages: 'packagePriceOverrides',
      upsells: 'upsellPriceOverrides',
    };

    const overrideField = overrideMap[key];
    if (!overrideField || !item?.[overrideField] || !Array.isArray(newValue)) {
      return newValue;
    }

    return newValue.map(entry => {
      const upgradeId = entry.upgradeId || entry._id || entry.value;
      const override = item[overrideField][upgradeId];
      if (override !== undefined && override !== null) {
        return { ...entry, priceOverride: override };
      }
      return entry;
    });
  }, []);

  // When priceOverrides change, update the corresponding packages/upsells arrays
  const transformBeforeSave = useCallback((key, processedValue, item) => {
    const targetMap = {
      packagePriceOverrides: 'packages',
      upsellPriceOverrides: 'upsells',
    };

    const targetField = targetMap[key];
    if (!targetField) return null;

    const items = item?.[targetField] || [];
    const updatedItems = items.map(entry => {
      const upgradeId = entry.upgradeId || entry._id || entry.value;
      const newEntry = { ...entry };
      delete newEntry.priceOverride;

      const override = processedValue[upgradeId];
      if (override !== undefined && override !== null) {
        newEntry.priceOverride = override;
      }

      return newEntry;
    });

    return { [targetField]: updatedItems };
  }, []);

  const renderPriceOverrideTable = useCallback(
    ({ value, setValue, isEditable, label, item, fieldSchema, apiOptions }) => {
      const sourceField = fieldSchema.sourceField;
      const sourceItems = sourceField ? item[sourceField] || [] : [];

      const getSourceOptions = () => {
        if (fieldSchema.getSourceOptions) {
          return fieldSchema.getSourceOptions(item, apiOptions);
        }
        return sourceItems.map(item => ({
          upgradeId: item.upgradeId || item._id || item.value,
          name: item.name || item.label || '',
          price: item.price || item.upgrade?.price || 0,
          upgrade: item.upgrade || item,
        }));
      };

      return (
        <PriceOverrideTable
          value={value}
          setValue={setValue}
          isEditable={isEditable}
          label={label}
          sourceField={sourceField}
          sourceOptions={getSourceOptions()}
        />
      );
    },
    []
  );

  const options = useMemo(
    () => ({
      titleKey: 'name',
      transformItems,
      transformFieldUpdate,
      transformBeforeSave,
      customRenderers: {
        'price-override-table': renderPriceOverrideTable,
      },
      onCreateClick: handleCreateClick,
    }),
    [
      transformItems,
      transformFieldUpdate,
      transformBeforeSave,
      renderPriceOverrideTable,
      handleCreateClick,
    ]
  );

  return (
    <Box sx={viewRootTextPrimarySx}>
      <EditableList
        schema={productSchema}
        title='Neues Produkt anlegen'
        resourceType='product'
        headerSearchPlaceholder='Produkte durchsuchen'
        parentIsEditable={parentIsEditable}
        options={options}
        leadingRowAction={item => ({
          tooltip: 'Wizard öffnen',
          right: 'product:update',
          icon: <WizardIcon fontSize='small' />,
          onClick: () => handleEditWithWizard(item),
        })}
      />

      {/* Choice Modal */}
      <Dialog open={showChoiceModal} onClose={handleCloseChoiceModal} maxWidth='sm' fullWidth>
        <DialogTitle>Produkt erstellen</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Wählen Sie, wie Sie das neue Produkt erstellen möchten:
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Paper
              onClick={handleChooseWizard}
              sx={{
                flex: 1,
                p: 3,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                border: '2px solid',
                borderColor: 'primary.main',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: 4,
                },
              }}
            >
              <WizardIcon sx={{ fontSize: 48 }} />
              <Typography variant='h6' align='center'>
                Wizard
              </Typography>
              <Typography variant='body2' align='center' sx={{ opacity: 0.9 }}>
                Geführter Prozess mit Entertainment, Preismatrix, Verfügbarkeiten und Booking Widget
              </Typography>
            </Paper>

            <Paper
              onClick={handleChooseStandard}
              sx={{
                flex: 1,
                p: 3,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                border: '2px solid',
                borderColor: 'divider',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'scale(1.02)',
                  borderColor: 'primary.main',
                  boxShadow: 2,
                },
              }}
            >
              <StandardIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography variant='h6' align='center'>
                Standard
              </Typography>
              <Typography variant='body2' align='center' color='text.secondary'>
                Klassisches Formular mit allen Feldern auf einen Blick
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChoiceModal}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      {/* Product Creation Wizard */}
      <ProductCreationWizard
        open={showWizard}
        onClose={handleWizardClose}
        onSuccess={handleWizardSuccess}
        existingProduct={editProduct}
      />

      {/* Standard Creation Dialog */}
      <Dialog open={showStandardDialog} onClose={handleCloseStandardDialog} maxWidth='sm' fullWidth>
        <DialogTitle>Neues Produkt anlegen (Standard)</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label='Interner Name'
                  value={standardFormData.internalName}
                  onChange={e =>
                    setStandardFormData(prev => ({ ...prev, internalName: e.target.value }))
                  }
                  fullWidth
                  required
                  size='small'
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Name'
                  value={standardFormData.name}
                  onChange={e => setStandardFormData(prev => ({ ...prev, name: e.target.value }))}
                  fullWidth
                  required
                  size='small'
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Beschreibung'
                  value={standardFormData.description}
                  onChange={e =>
                    setStandardFormData(prev => ({ ...prev, description: e.target.value }))
                  }
                  fullWidth
                  multiline
                  rows={2}
                  size='small'
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label='Gültig ab'
                  type='date'
                  value={standardFormData.validFrom}
                  onChange={e =>
                    setStandardFormData(prev => ({ ...prev, validFrom: e.target.value }))
                  }
                  fullWidth
                  required
                  size='small'
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label='Gültig bis'
                  type='date'
                  value={standardFormData.validTo}
                  onChange={e =>
                    setStandardFormData(prev => ({ ...prev, validTo: e.target.value }))
                  }
                  fullWidth
                  required
                  size='small'
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStandardDialog} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleStandardSave}
            disabled={
              isSaving || !standardFormData.internalName.trim() || !standardFormData.name.trim()
            }
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
          >
            {isSaving ? 'Speichern...' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProductManagement;
